"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot, Send, X } from "lucide-react";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const SALUT = `Bienvenue à l'Empire d'Adji Anta Diop. Je suis le Chambellan Impérial — je vous guide vers les coffrets Mawlid Naby – Gamou de Bouye de Ndiamsyl, les 13 marques, les packs e-commerce ou les portails partenaires. Que puis-je pour vous ?`;

const SUGGESTIONS = [
  "Quels coffrets offrir pour le Mawlid ?",
  "Présente-moi les 13 marques",
  "C'est quoi la signature de l'Empire ?",
];

export function ChatbotImperial() {
  const [ouvert, setOuvert] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{ role: "assistant", content: SALUT }]);
  const [saisie, setSaisie] = useState("");
  const [enCours, setEnCours] = useState(false);
  const finRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, enCours, ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOuvert(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [ouvert]);

  const envoyer = async (texte?: string) => {
    const contenu = (texte ?? saisie).trim();
    if (!contenu || enCours) return;
    const suivants: Message[] = [...messages, { role: "user", content: contenu }];
    setMessages(suivants);
    setSaisie("");
    setEnCours(true);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: suivants }),
      });
      const data = await res.json();
      setMessages((prev) => [...prev, { role: "assistant", content: data?.reply ?? "Le palais ne répond pas — réessayez." }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Le réseau du palais tremble — réessayez dans un instant." }]);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setOuvert((v) => !v)}
        aria-label={ouvert ? "Fermer le Chambellan Impérial" : "Ouvrir le Chambellan Impérial"}
        aria-expanded={ouvert}
        className="fixed bottom-5 right-5 z-[70] h-14 w-14 rounded-full gold-btn border border-[#F6E27A]/60 flex items-center justify-center shadow-[0_10px_36px_rgba(212,175,55,0.45)] focus:outline-none focus:ring-2 focus:ring-[#F6E27A]/70"
      >
        {ouvert ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>

      {/* Panneau de conversation */}
      <AnimatePresence>
        {ouvert && (
          <motion.aside
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            role="dialog"
            aria-label="Conversation avec le Chambellan Impérial"
            className="fixed bottom-24 right-5 z-[70] w-[92vw] max-w-sm rounded-2xl border border-[#D4AF37]/45 bg-[#04180f] shadow-[0_30px_90px_rgba(0,0,0,0.75)] overflow-hidden flex flex-col"
            style={{ maxHeight: "min(560px, 72vh)" }}
          >
            <div className="px-4 py-3 border-b border-[#D4AF37]/30 bg-[#0a3527]/60 flex items-center gap-3">
              <span className="h-9 w-9 rounded-full gold-btn flex items-center justify-center shrink-0">
                <Bot className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="font-display font-bold text-[#F6E27A] leading-tight">Le Chambellan Impérial</p>
                <p className="text-[11px] text-[#F5EFE0]/55">Serveur du palais · répond en français</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto empire-scroll px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={
                    "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed " +
                    (m.role === "user"
                      ? "ml-auto bg-[#8E0E2B]/85 border border-[#D4AF37]/35 text-[#FBEDEE] rounded-br-sm"
                      : "mr-auto bg-[#0a3527]/70 border border-[#D4AF37]/30 text-[#F5EFE0] rounded-bl-sm")
                  }
                >
                  {m.content}
                </div>
              ))}
              {enCours && (
                <div className="mr-auto flex items-center gap-1.5 px-4 py-3 rounded-2xl bg-[#0a3527]/70 border border-[#D4AF37]/30" aria-label="Le Chambellan écrit">
                  <span className="h-2 w-2 rounded-full bg-[#F6E27A] animate-bounce [animation-delay:0ms]" />
                  <span className="h-2 w-2 rounded-full bg-[#F6E27A] animate-bounce [animation-delay:150ms]" />
                  <span className="h-2 w-2 rounded-full bg-[#F6E27A] animate-bounce [animation-delay:300ms]" />
                </div>
              )}
              <div ref={finRef} />
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => envoyer(s)}
                    className="text-[11px] px-3 py-1.5 rounded-full border border-[#D4AF37]/40 text-[#F6E27A] hover:bg-[#D4AF37]/15 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                envoyer();
              }}
              className="border-t border-[#D4AF37]/30 bg-[#03150d]/80 p-3 flex items-center gap-2"
            >
              <label htmlFor="chambellan-saisie" className="sr-only">
                Votre message au Chambellan
              </label>
              <input
                id="chambellan-saisie"
                value={saisie}
                onChange={(e) => setSaisie(e.target.value)}
                placeholder="Écrivez au palais…"
                autoComplete="off"
                className="flex-1 bg-[#04180f] border border-[#D4AF37]/35 rounded-xl px-4 py-2.5 text-sm text-[#F5EFE0] placeholder:text-[#F5EFE0]/35 focus:outline-none focus:border-[#F6E27A]/70"
              />
              <button
                type="submit"
                disabled={enCours || saisie.trim().length === 0}
                aria-label="Envoyer le message"
                className="h-10 w-10 rounded-xl gold-btn flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
